import { createContext, useContext, useState, useCallback } from 'react';
import { useAuthStore } from '@/entities/session/model/authStore';
import {
  ROLES,
  getRoleForDeployment,
  isRipikAdmin as checkIsRipikAdmin,
  isRipikStaff as checkIsRipikStaff,
  canViewSettings as checkCanViewSettings,
} from '@/entities/rbac/lib/permissions';

/**
 * Deployment-scoped role context.
 *
 * Shape:
 *   role           – role string for active deployment | null
 *   canManage      – true for ripik_admin | client_admin (internal CRUD: users/params/layers)
 *   canViewSettings– true for ripik_admin | client_admin (access to settings tabs)
 *   isRipikStaff   – true for ripik_admin | ripik_user
 *   isRipikAdmin   – true for ripik_admin only (deployment-level CRUD: create/edit/delete)
 */

const DeploymentRoleCtx = createContext({
  deploymentId: null,
  role: null,
  canManage: false,
  canViewSettings: false,
  isRipikStaff: false,
  isRipikAdmin: false,
  setActiveDeployment: () => {},
});

export function DeploymentRoleProvider({ children }) {
  const { assignments } = useAuthStore();
  const [deploymentId, setDeploymentId] = useState(null);

  const setActiveDeployment = useCallback((id) => {
    setDeploymentId(id ?? null);
  }, []);

  const role = deploymentId ? getRoleForDeployment(assignments, deploymentId) : null;
  const ripikStaff = checkIsRipikStaff(assignments);
  const ripikAdmin = checkIsRipikAdmin(assignments);
  // canManage: internal CRUD within a deployment (users, parameters, layers)
  // ripik_admin and client_admin can manage; ripik_user and client_user cannot
  const canManage = ripikAdmin || role === ROLES.CLIENT_ADMIN;
  const viewSettings = deploymentId ? checkCanViewSettings(assignments, deploymentId) : false;

  return (
    <DeploymentRoleCtx.Provider value={{
      deploymentId,
      role,
      canManage,
      canViewSettings: viewSettings,
      isRipikStaff: ripikStaff,
      isRipikAdmin: ripikAdmin,
      setActiveDeployment,
    }}>
      {children}
    </DeploymentRoleCtx.Provider>
  );
}

export function useDeploymentRole() {
  return useContext(DeploymentRoleCtx);
}
